/**
 * How much of the machine to spend.
 *
 * This runs on phones, and a phone is not one machine — the same page has to
 * hold a steady frame on a four-year-old Android and still look like something
 * on a desktop with a discrete card. Two things do most of that work, and
 * neither is a graphics feature: how many pixels are drawn, and how much
 * geometry is in front of the camera.
 *
 * So there are three tiers, picked from what the browser will admit about the
 * device, and a governor on top that watches how long frames actually take and
 * moves the resolution to suit. The governor matters more than the tier: a
 * guess about the hardware is a guess, and 16.7 milliseconds is a measurement.
 * It reacts to a sustained cost rather than to one slow frame, because a single
 * long frame is usually a level loading and dropping the resolution for that is
 * how you end up permanently blurry.
 */

export type Tier = 'mobile' | 'balanced' | 'high';

export interface Quality {
  tier: Tier;
  /** Ceiling on device pixel ratio before the governor scales it. */
  pixelRatio: number;
  /** Lower bound the governor may scale to. */
  minScale: number;
  antialias: boolean;
  /** Triangle budget for the prop pass. */
  props: number;
  /** How far the surrounding town is built out, in plots. */
  scenery: number;
  /** Live tracer and blast effects at once. */
  effects: number;
  /** Whether the sun casts a shadow map, and how big. */
  shadows: number;
}

/*
 * Render at the panel's resolution, and let the governor take it back.
 *
 * Mobile was pinned to a device pixel ratio of 1.0. A phone panel is 3, so the
 * game was drawing at a third of the resolution of the screen it was on and
 * every edge in it stair-stepped — which is not a subtle thing on geometry
 * that is nothing but straight edges meeting at right angles. It is the first
 * thing anybody would say about it on a phone, and somebody did.
 *
 * The ceiling goes up rather than the floor: `minScale` is set so that the
 * *worst* the governor can settle on is the ratio this used to start at, and
 * it only goes there if the frames actually demand it. So a modern phone gets
 * a sharp picture, an old one ends up exactly where it was before, and neither
 * needs to be identified in advance — which is the whole reason there is a
 * governor rather than a device table.
 *
 * Antialiasing stays off below `high`: at twice the pixels there is little
 * left for MSAA to fix, and it is the more expensive way to buy it.
 */
const TIERS: Record<Tier, Omit<Quality, 'tier'>> = {
  mobile:   { pixelRatio: 2.0, minScale: 0.5,  antialias: false, props: 16000, scenery: 0.55, effects: 24, shadows: 0 },
  balanced: { pixelRatio: 2.0, minScale: 0.65, antialias: false, props: 34000, scenery: 0.8,  effects: 48, shadows: 0 },
  high:     { pixelRatio: 2.0, minScale: 0.8,  antialias: true,  props: 60000, scenery: 1.0,  effects: 96, shadows: 2048 },
};

/**
 * What this device probably is.
 *
 * Deliberately pessimistic. Guessing high on a slow phone costs the player a
 * playable frame rate; guessing low on a fast desktop costs them some pixels
 * for a few seconds until the governor notices there is headroom and takes
 * them back.
 */
export function detectTier(): Tier {
  const forced = new URLSearchParams(location.search).get('q');
  if (forced === 'mobile' || forced === 'balanced' || forced === 'high') return forced;

  const touch = !matchMedia('(pointer: fine)').matches || navigator.maxTouchPoints > 0;
  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = (navigator as { deviceMemory?: number }).deviceMemory ?? (touch ? 4 : 8);
  const pixels = innerWidth * innerHeight * Math.min(devicePixelRatio, 2) ** 2;

  if (touch) {
    // A phone that is both many-cored and generous with memory is a recent
    // one; everything else gets the safe tier.
    return cores >= 6 && memory >= 6 && pixels < 4.2e6 ? 'balanced' : 'mobile';
  }
  return cores >= 8 && memory >= 8 ? 'high' : 'balanced';
}

export function qualityFor(tier: Tier): Quality {
  return { tier, ...TIERS[tier] };
}

/**
 * Watch the clock and move the resolution.
 *
 * Frame time, not frame rate: rate is an average and averages hide the frames
 * that make a game feel bad. The median of the last two seconds is compared
 * against the budget, and the scale moves a few per cent at a time so a change
 * is never something the player sees happen.
 */
export class Governor {
  /**
   * Multiplier on the tier's pixel ratio, 0..1. Starts below full on purpose.
   *
   * The tier's ratio is a *ceiling* — the sharpest this machine is allowed to
   * get — and it was raised to 2.0 so a retina panel could be drawn at its own
   * resolution. Opening there is the wrong way round: a desktop that used to
   * start at 1.35 suddenly began at 2.0, which is 2.2 times the fragments, and
   * a machine that cannot hold that spends the first several seconds of every
   * floor dropping frames while this walks it back down eight per cent at a
   * time.
   *
   * So it opens at three quarters and climbs when there is headroom, which is
   * the rule the file already stated and which raising the ceiling quietly
   * broke: guessing low costs some pixels for a second or two, guessing high
   * costs a playable frame rate for longer than anybody will wait.
   */
  scale = 0.75;
  /** Milliseconds a frame is allowed to take. 60 fps with room to spare. */
  readonly budget = 15.5;

  private samples: number[] = [];
  private cooldown = 0;
  private quality: Quality;

  constructor(quality: Quality) {
    this.quality = quality;
  }

  /**
   * The player picked a different tier.
   *
   * The scale is kept rather than reset: whatever headroom the machine had a
   * second ago it still has, and starting again from full resolution means a
   * visible stutter every time somebody touches the setting.
   */
  retier(quality: Quality): void {
    this.quality = quality;
    this.scale = Math.max(quality.minScale, Math.min(1, this.scale));
    this.samples.length = 0;
  }

  /** Returns the pixel ratio to render at, or null when nothing has changed. */
  sample(dtMs: number, now: number): number | null {
    // A frame that took longer than a fifth of a second was not a frame, it was
    // a level being built. Those tell you nothing about steady-state cost.
    if (dtMs < 200) this.samples.push(dtMs);
    // Sixty frames rather than ninety: at 120Hz ninety frames is most of a
    // second before the first correction, and the whole point is to be quick
    // about the direction that hurts.
    if (this.samples.length < 60 || now < this.cooldown) return null;

    const sorted = this.samples.slice().sort((a, b) => a - b);
    const median = sorted[sorted.length >> 1];
    const p90 = sorted[Math.floor(sorted.length * 0.9)];
    this.samples.length = 0;
    this.cooldown = now + 1200;

    const floor = this.quality.minScale;
    let next = this.scale;
    // Down on a sustained overrun, up only when there is real headroom, and
    // never both in the same window.
    // Down in bigger steps than up: being too sharp to run is a fault the
    // player is feeling right now, being too soft is one they are not.
    if (median > this.budget * 1.12 || p90 > this.budget * 1.9) next = Math.max(floor, this.scale - 0.12);
    else if (median < this.budget * 0.72 && p90 < this.budget * 1.15) next = Math.min(1, this.scale + 0.05);
    if (Math.abs(next - this.scale) < 0.001) return null;
    this.scale = next;
    return this.pixelRatio;
  }

  get pixelRatio(): number {
    return Math.min(devicePixelRatio, this.quality.pixelRatio) * this.scale;
  }
}
